import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { EmailPreviewDto } from '../../../data-transfer-objects/abuse-case.dto';

/** What one action's confirmation dialog needs to show. */
export interface ActionConfirmData {
  /** "Block this account?" - a question, so the dialog reads as a decision. */
  title: string;
  /** Plain language: what happens the moment Confirm is clicked. */
  description: string;
  confirmLabel: string;
  /** Red confirm button, for an action that bans, upholds, or deletes data. */
  danger?: boolean;
  /**
   * The email this action sends, already fetched. Absent entirely for an
   * action that sends nothing (Unblock, Dismiss) - the dialog then shows no
   * email section at all, rather than an empty one that invites wondering
   * whether something was supposed to be there.
   */
  emailPreview?: EmailPreviewDto | null;
  /**
   * Ask for a free-text reason. Present for Block, where the admin is acting
   * on their own read of an account rather than on the guard's numbers: what
   * they saw is the only record of why, and it is not written anywhere else.
   * Optional to fill in - a reason nobody typed is better than a reason typed
   * to get past a required field.
   */
  reason?: { label: string; hint?: string };
}

/** What closing the dialog with Confirm hands back to the caller. */
export interface ActionConfirmResult {
  confirmed: true;
  /** The edited subject and body, only present when this action emails. */
  email?: { subject: string; text: string };
  /** What the admin typed, trimmed; absent when the field was empty or not shown. */
  reason?: string;
}

/**
 * One dialog, reused by every case action.
 *
 * The point of it is that nothing fires blind. Block, Unblock, Dismiss,
 * Restore and Uphold each open this before doing anything, so the admin reads
 * what is about to happen - and, for the three that email somebody, reads and
 * can edit exactly what that person is about to receive. An admin who sends a
 * different message than the default chose to; they are not guessing at what
 * went out after the fact.
 *
 * The dialog itself makes no network calls. The caller fetches the preview
 * (when there is one) before opening it, so this component only ever renders
 * data it was handed - simpler to reason about, and nothing here can retry a
 * fetch and confuse "loading the preview" with "sending the action".
 */
@Component({
  selector: 'app-action-confirm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './action-confirm-dialog.component.html',
  styleUrls: ['./action-confirm-dialog.component.scss'],
})
export class ActionConfirmDialogComponent {
  subject: string;
  text: string;
  reason = '';

  constructor(
    private dialogRef: MatDialogRef<ActionConfirmDialogComponent, ActionConfirmResult>,
    @Inject(MAT_DIALOG_DATA) public data: ActionConfirmData
  ) {
    this.subject = data.emailPreview?.subject ?? '';
    this.text = data.emailPreview?.text ?? '';
  }

  get asksReason(): boolean {
    return Boolean(this.data.reason);
  }

  get sendsEmail(): boolean {
    return this.data.emailPreview !== undefined && this.data.emailPreview !== null;
  }

  get hasAddress(): boolean {
    return Boolean(this.data.emailPreview?.to);
  }

  get edited(): boolean {
    if (!this.data.emailPreview) return false;
    return (
      this.subject !== this.data.emailPreview.subject ||
      this.text !== this.data.emailPreview.text
    );
  }

  resetEmail(): void {
    if (!this.data.emailPreview) return;
    this.subject = this.data.emailPreview.subject;
    this.text = this.data.emailPreview.text;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    const email =
      this.sendsEmail && this.hasAddress
        ? { subject: this.subject, text: this.text }
        : undefined;
    const reason = this.asksReason && this.reason.trim() ? this.reason.trim() : undefined;
    this.dialogRef.close({ confirmed: true, email, reason });
  }
}
